import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import splashIcon from "../assets/images/splash-icon.png";
import splashIconDark from "../assets/images/ToronQ Logos-15.png";
import {
  Building2 as FaBuilding,
  Lock as FaLock,
  MessageSquare as FaComments,
  Shield as FaShieldAlt,
  MapPin as FaMapPin,
  CheckCircle as FaCheckCircle,
} from "lucide-react";
import { connectBusinessApi, addLocationToBusinessApi } from "../api/business.api";
import {
  getOnboardingGoogleAuthUrlApi,
  selectOnboardingLocationsApi,
} from "../api/onboarding.api";
import {
  extractVerificationException,
  persistVerificationException,
} from "../utils/verificationException";
import useBusinessStore from "../store/businessStore";
import {
  getBusinessId,
  getBusinessName,
  getBusinessAddress,
  getBusinessRating,
  getBusinessReviewCount,
  normalizeBusiness,
} from "../utils/business";
import { showError, showSuccess } from "../utils/toast";
import { useTheme } from "../context/ThemeContext";
import Button from "../components/ui/Button";
import type { ApiError } from "../types/api";
import type { RawBusiness } from "../types/business";

const steps = [
  {
    id: 1,
    title: "Authorize Access",
    description:
      "Sign in with Google to grant ToronQ AI permission to manage your reviews.",
    icon: <FaLock />,
  },
  {
    id: 2,
    title: "Select Your Profile",
    description: "Choose which Google Business Profile you want to connect.",
    icon: <FaBuilding />,
  },
  {
    id: 3,
    title: "Start Replying",
    description: "AI will draft replies and you approve them with one tap.",
    icon: <FaComments />,
  },
];

const DEV_MOCK_LOCATIONS = [
  {
    id: "dev-loc-1",
    placeId: "dev-place-1",
    name: "Sarah's Kitchen - Maidstone",
    address: "3 Penfold Gardens, Maidstone, Kent",
    averageRating: 4.6,
    totalRatings: 342,
  },
  {
    id: "dev-loc-2",
    placeId: "dev-place-2",
    name: "Sarah's Kitchen - London",
    address: "14 Holborn Street, London",
    averageRating: 4.4,
    totalRatings: 221,
  },
];

const normalizeOnboardingAuthUrl = (authUrl: string): string => {
  if (!authUrl) return authUrl;

  const frontendCallback = `${window.location.origin}/oauth/callback`;

  const rewriteRawString = (value: string): string => {
    if (!value) return value;

    let rewritten = String(value);
    rewritten = rewritten
      .replaceAll("/api/auth/google-callback", "/oauth/callback")
      .replaceAll("/api/businesses/oauth-callback", "/oauth/callback");

    const encodedAuthCallback = encodeURIComponent("/api/auth/google-callback");
    const encodedBusinessCallback = encodeURIComponent("/api/businesses/oauth-callback");
    const encodedFrontendCallback = encodeURIComponent("/oauth/callback");

    rewritten = rewritten
      .replaceAll(encodedAuthCallback, encodedFrontendCallback)
      .replaceAll(encodedBusinessCallback, encodedFrontendCallback);

    const fullAuthCallback = "https://api.toronq.ai/api/auth/google-callback";
    const fullBusinessCallback = "https://api.toronq.ai/api/businesses/oauth-callback";
    rewritten = rewritten
      .replaceAll(fullAuthCallback, frontendCallback)
      .replaceAll(fullBusinessCallback, frontendCallback)
      .replaceAll(encodeURIComponent(fullAuthCallback), encodeURIComponent(frontendCallback))
      .replaceAll(encodeURIComponent(fullBusinessCallback), encodeURIComponent(frontendCallback));

    return rewritten;
  };

  try {
    const parsed = new URL(rewriteRawString(authUrl));
    const redirectUri = parsed.searchParams.get("redirect_uri");

    if (!redirectUri) return rewriteRawString(authUrl);

    const shouldRewrite =
      redirectUri.includes("/api/auth/google-callback") ||
      redirectUri.includes("/api/businesses/oauth-callback");

    if (!shouldRewrite) return rewriteRawString(authUrl);

    parsed.searchParams.set("redirect_uri", frontendCallback);
    return parsed.toString();
  } catch {
    return rewriteRawString(authUrl);
  }
};

const getRedirectUriFromGoogleAuthUrl = (authUrl: string) => {
  if (!authUrl) return "";

  try {
    const parsed = new URL(authUrl);
    return parsed.searchParams.get("redirect_uri") || "";
  } catch {
    return "";
  }
};

export default function ConnectGoogle() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, mode: themeMode } = useTheme();
  // Neutral border for structure — theme.border carries a blue tint meant for
  // accent surfaces; this page uses it only where blue is intentional.
  const subtleBorder = themeMode === "dark" ? theme.border : "#E7EAF0";
  const { connectedBusinesses, setConnectedBusinesses, setActiveBusiness } =
    useBusinessStore();

  // Support both single business and multiple locations
  const isMultiLocation = location.state?.isMultiLocation || false;
  const locations = location.state?.locations ||
    JSON.parse(localStorage.getItem("selected_locations") || "[]");

  const business =
    location.state?.business ||
    JSON.parse(localStorage.getItem("selected_business") || "null");

  // Get mode and parent business for add-location flow
  const mode = location.state?.mode || "new-business";
  const parentBusiness = location.state?.parentBusiness || null;
  const isAddLocationMode = mode === "add-location" && parentBusiness;
  const onboardingContext =
    location.state?.onboardingContext ||
    JSON.parse(localStorage.getItem("onboarding_context") || "null");
  const roleLabel = onboardingContext?.roleLabel || "Business";
  const isOnboardingMode = !isAddLocationMode;
  const isDevBypass =
    process.env.NODE_ENV === "development" &&
    localStorage.getItem("toronq-dev-onboarding-bypass") === "true";
  const discoveredLocations =
    location.state?.discoveredLocations ||
    JSON.parse(localStorage.getItem("onboarding_discovered_locations") || "[]");

  // Use locations array if multi-location, otherwise single business
  const selectedLocations = isOnboardingMode
    ? discoveredLocations
    : isMultiLocation
    ? locations
    : business
    ? [business]
    : [];

  const [isLoading, setIsLoading] = useState(false);
  const [connectingIndex, setConnectingIndex] = useState(-1);
  const [connectedCount, setConnectedCount] = useState(0);
  const [error, setError] = useState("");

  const handleConnectGoogle = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setError("");

      if (isOnboardingMode && selectedLocations.length === 0) {
        if (isDevBypass) {
          localStorage.setItem("onboarding_discovered_locations", JSON.stringify(DEV_MOCK_LOCATIONS));
          showSuccess("Dev bypass: mock locations discovered");
          navigate("/connect-google", {
            replace: true,
            state: {
              onboardingContext,
              discoveredLocations: DEV_MOCK_LOCATIONS,
            },
          });
          return;
        }

        const authResponse = await getOnboardingGoogleAuthUrlApi();
        const authUrl =
          authResponse?.data?.authorizationUrl ||
          authResponse?.data?.authUrl ||
          authResponse?.data?.url ||
          authResponse?.authorizationUrl ||
          authResponse?.authUrl ||
          authResponse?.url;

        if (!authUrl) {
          throw new Error("Unable to get Google authorization URL.");
        }

        const normalizedAuthUrl = normalizeOnboardingAuthUrl(authUrl);
        const expectedCallback = `${window.location.origin}/oauth/callback`;
        const redirectUri = getRedirectUriFromGoogleAuthUrl(normalizedAuthUrl);

        if (
          normalizedAuthUrl.includes("/api/auth/google-callback") ||
          normalizedAuthUrl.includes(encodeURIComponent("/api/auth/google-callback")) ||
          normalizedAuthUrl.includes(encodeURIComponent("https://api.toronq.ai/api/auth/google-callback"))
        ) {
          const configError =
            "Google auth redirect is misconfigured on backend (auth callback). Please update onboarding auth-url redirect_uri to /oauth/callback.";
          setError(configError);
          showError(configError);
          localStorage.setItem("onboarding_auth_url_last", normalizedAuthUrl);
          return;
        }

        if (redirectUri !== expectedCallback) {
          const configError =
            "Google auth redirect_uri did not match expected onboarding callback. Backend onboarding auth-url must return redirect_uri as frontend /oauth/callback.";
          setError(configError);
          showError(configError);
          localStorage.setItem("onboarding_auth_url_last", normalizedAuthUrl);
          return;
        }

        window.location.assign(normalizedAuthUrl);
        return;
      }

      if (isOnboardingMode) {
        if (isDevBypass) {
          localStorage.setItem("onboarding_selected_locations", JSON.stringify(selectedLocations));
          localStorage.removeItem("onboarding_discovered_locations");

          navigate("/import-sync", {
            replace: true,
            state: {
              onboardingContext,
              selectedLocations,
              selectionResponse: { success: true, mocked: true },
            },
          });
          return;
        }

        const accessToken = localStorage.getItem("onboarding_google_access_token");
        const response = await selectOnboardingLocationsApi({
          accessToken,
          locationNames: selectedLocations.map((loc: RawBusiness & { locationName?: string }) => loc?.name || loc?.locationName || getBusinessId(loc)),
        });

        localStorage.setItem("onboarding_selected_locations", JSON.stringify(selectedLocations));
        localStorage.removeItem("onboarding_discovered_locations");
        localStorage.removeItem("onboarding_google_access_token");

        navigate("/import-sync", {
          replace: true,
          state: {
            onboardingContext,
            selectedLocations,
            selectionResponse: response?.data || response,
          },
        });
        return;
      }

      // Add-location mode keeps the existing connect endpoint.
      // New business must go through onboarding discovery/select flow.
      if (!isAddLocationMode) {
        navigate("/onboarding", { replace: true });
        return;
      }

      // Get kybVerificationId if available
      const kybVerificationId = localStorage.getItem("kyc_id") || location.state?.kybVerificationId;
      console.log("🔍 ConnectGoogle debug:", {
        kybIdFromStorage: localStorage.getItem("kyc_id"),
        kybIdFromState: location.state?.kybVerificationId,
        finalKybId: kybVerificationId,
        allLocalStorage: Object.keys(localStorage),
      });

      let lastOAuthUrl = null;
      let succeededCount = 0;
      const failedLocations = [];
      const newConnectedBusinesses = [...connectedBusinesses];

      // Connect each location
      for (let i = 0; i < selectedLocations.length; i++) {
        const loc = selectedLocations[i];
        setConnectingIndex(i);

        // Payload for new business connection
        const newBusinessPayload = {
          placeId: getBusinessId(loc),
          name: getBusinessName(loc),
          address: getBusinessAddress(loc),
          averageRating: getBusinessRating(loc),
          totalRatings: getBusinessReviewCount(loc),
          ...(kybVerificationId ? { kybVerificationId } : {}),
        };

        console.log(`📊 kybVerificationId check:`, { kybVerificationId, isTruthy: !!kybVerificationId, type: typeof kybVerificationId });
        console.log(`📤 Sending to /api/businesses/connect for ${getBusinessName(loc)}:`, JSON.stringify(newBusinessPayload));

        // Payload for adding location to existing business (includes businessId)
        const addLocationPayload = {
          placeId: getBusinessId(loc),
          name: getBusinessName(loc),
          address: getBusinessAddress(loc),
          averageRating: getBusinessRating(loc),
          totalRatings: getBusinessReviewCount(loc),
          ...(kybVerificationId && { kybVerificationId }),
          ...(parentBusiness?.id && { businessId: parentBusiness.id }),
        };

        try {
          console.log(`🔗 ${isAddLocationMode ? "Adding location to" : "Connecting"} business:`, getBusinessName(loc));
          
          // Use appropriate API based on mode
          const response = isAddLocationMode
            ? await addLocationToBusinessApi(parentBusiness.id, addLocationPayload)
            : await connectBusinessApi(newBusinessPayload);

          console.log(`✅ Response:`, response);
          
          const connectedBusiness = normalizeBusiness(
            response?.data?.business || response?.business || newBusinessPayload,
          );

          // Add to connected businesses (avoid duplicates)
          const existingIndex = newConnectedBusinesses.findIndex(
            (item) => String(getBusinessId(item)) === String(getBusinessId(connectedBusiness))
          );
          if (existingIndex >= 0) {
            newConnectedBusinesses[existingIndex] = connectedBusiness;
          } else {
            newConnectedBusinesses.unshift(connectedBusiness);
          }

          succeededCount += 1;
          setConnectedCount((prev) => prev + 1);

          // Track OAuth URL if needed (use the last one that requires OAuth)
          if (response?.data?.requiresOAuth && response?.data?.oauthUrl) {
            lastOAuthUrl = response.data.oauthUrl;
            // Store all connected locations for after OAuth
            localStorage.setItem("pending_oauth_business", JSON.stringify(connectedBusiness));
          }
        } catch (rawErr) {
          const err = rawErr as ApiError;
          // KYB/2FA/Company Email 403 errors must bubble up to the outer catch — do not swallow them
          if (
            err?.response?.status === 403 &&
            /KYB|identity verification|Company email verification|2FA|two.factor/i.test(err?.response?.data?.message || "")
          ) {
            throw err;
          }

          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            `Failed to ${isAddLocationMode ? "add location" : "connect business"}`;

          console.error(`❌ Failed to ${isAddLocationMode ? "add location to" : "connect"} ${getBusinessName(loc)}:`, err);

          failedLocations.push({ name: getBusinessName(loc), message: errorMessage });
          // Continue with other locations even if one fails
        }
      }

      setConnectedBusinesses(newConnectedBusinesses);

      // Set the first connected as active
      if (newConnectedBusinesses.length > 0) {
        setActiveBusiness(newConnectedBusinesses[0]);
      }

      // Show accurate feedback based on actual results
      if (succeededCount > 0) {
        // Only mark as fully connected if no OAuth redirect is pending.
        // When OAuth is required, the connection isn't real until
        // OAuthCallback.js confirms it - flagging it here would be premature.
        if (!lastOAuthUrl) {
          localStorage.setItem("google_connected", "true");
          localStorage.removeItem("google_connect_skipped");
        }
        localStorage.removeItem("selected_locations");
        localStorage.removeItem("selected_business");

        showSuccess(
          `${succeededCount} location${succeededCount > 1 ? "s" : ""} connected successfully`
        );
      }

      // Surface any per-location failures with the real API message
      if (failedLocations.length > 0) {
        failedLocations.forEach(({ name, message }) => {
          showError(`${name}: ${message}`);
        });
      }

      // If nothing succeeded, stop here and let the user fix the issue
      if (succeededCount === 0) {
        setError(failedLocations[0]?.message || "Unable to connect Google Business Profile.");
        return;
      }

      // If OAuth is required, redirect
      if (lastOAuthUrl) {
        localStorage.setItem(
          "pending_oauth_meta",
          JSON.stringify({
            onboardingContext,
            connectedCount: succeededCount,
          }),
        );
        window.location.href = lastOAuthUrl;
        return;
      }

      navigate("/import-sync", {
        state: {
          business: newConnectedBusinesses[0],
          onboardingContext,
          connectedCount: succeededCount,
        },
      });
    } catch (rawErr) {
      const err = rawErr as ApiError;
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        "Unable to connect Google Business Profile.";

      const verificationException = extractVerificationException(err);
      if (verificationException) {
        persistVerificationException(verificationException, {
          stage: "connect-google",
          mode,
          isOnboardingMode,
          selectedLocationCount: selectedLocations.length,
        });
      }

      // KYB verification required — redirect to verification flow
      if (
        verificationException ||
        (status === 403 && /KYB|identity verification|Company email verification/i.test(message))
      ) {
        // Persist the connect payload so the user can retry after KYB approval
        localStorage.setItem(
          "kyb_pending_connect_payload",
          JSON.stringify({ selectedLocations, isAddLocationMode }),
        );
        navigate("/kyb-verification", {
          state: {
            verificationException,
            source: "connect-google",
          },
        });
        return;
      }

      // 2FA required — session was created before 2FA was enabled
      if (status === 403 && /2FA|two.factor/i.test(message)) {
        setError("Your session was created before 2FA was enabled. Please log out and log back in using 2FA to continue.");
        showError("Please log out and log back in with 2FA to connect your business.");
        return;
      }

      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
      setConnectingIndex(-1);
    }
  };

  const handleSkip = () => {
    if (isOnboardingMode) {
      navigate("/dashboard/overview", { replace: true });
      return;
    }

    if (selectedLocations.length > 0) {
      setActiveBusiness(selectedLocations[0]);
    }

    // Clear any stale connection state from a previous abandoned attempt
    // so Pulse Check doesn't mistakenly treat this business as connected.
    localStorage.removeItem("google_connected");
    localStorage.removeItem("pending_oauth_business");
    localStorage.setItem("google_connect_skipped", "true");

    navigate("/dashboard/overview");
  };

  return (
    <div className="flex min-h-screen justify-center px-4 py-8" style={{ background: theme.background }}>
      <div className="w-full max-w-xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-8 text-sm font-semibold"
          style={{ color: theme.primary }}
        >
          ← Back
        </button>

        <div className="text-center">
          <img
            src={themeMode === "dark" ? splashIconDark : splashIcon}
            alt="logo"
            className="mx-auto mt-6 h-16 w-auto object-contain"
          />

          <h1 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: theme.text }}>
            {isOnboardingMode
              ? selectedLocations.length > 0
                ? "Select Locations to Connect"
                : "Connect Your Google Business Profile"
              : isAddLocationMode
              ? `Add Location to ${parentBusiness?.name}`
              : "Connect Your Google Business"}
          </h1>

          <p className="mt-2 text-sm leading-relaxed" style={{ color: theme.textMuted }}>
            {isOnboardingMode
              ? selectedLocations.length > 0
                ? `Step 6: ${roleLabel} flow. Review and connect the locations discovered from Google Business Profile.`
                : `Step 6: ${roleLabel} flow. Authorize Google so we can discover your business locations.`
              : isAddLocationMode
              ? "Connect this location to your existing business. You'll need to authorize Google access."
              : `Step 6: ${roleLabel} flow. Authenticate your Google Business Profile and choose what to connect.`}
          </p>
        </div>

        {selectedLocations.length > 0 ? (
          <div className="mt-6 space-y-3">
            <p className="text-sm font-semibold" style={{ color: theme.primary }}>
              {selectedLocations.length === 1 ? "Selected Location" : `${selectedLocations.length} Locations Selected`}
            </p>

            <div className="max-h-48 space-y-2 overflow-y-auto rounded-2xl border p-3" style={{ borderColor: subtleBorder, background: theme.card }}>
              {selectedLocations.map((loc: RawBusiness, index: number) => (
                <div
                  key={getBusinessId(loc) || index}
                  className="flex items-start gap-3 rounded-xl p-3"
                  style={{
                    background:
                      connectingIndex === index
                        ? theme.infoBg
                        : index < connectedCount
                        ? theme.successBg
                        : theme.secondary,
                  }}
                >
                  <div className="mt-0.5">
                    {index < connectedCount ? (
                      <FaCheckCircle size={18} color={theme.success} />
                    ) : connectingIndex === index ? (
                      <span className="block h-4 w-4 animate-spin rounded-full border-2" style={{ borderColor: "rgba(2,34,241,0.3)", borderTopColor: theme.primary }} />
                    ) : (
                      <FaMapPin size={18} color={theme.primary} />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold" style={{ color: theme.text }}>
                      {getBusinessName(loc)}
                    </h3>
                    <p className="mt-0.5 truncate text-xs" style={{ color: theme.textLight }}>
                      {getBusinessAddress(loc)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : isOnboardingMode ? (
          <div className="mt-6 rounded-2xl border p-4" style={{ borderColor: subtleBorder, background: theme.card }}>
            <p className="text-sm font-semibold" style={{ color: theme.text }}>
              Google authorization is required before we can discover your locations.
            </p>
            <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
              {isDevBypass
                ? "Dev bypass is enabled. Click continue to load mock locations without Google sign-in."
                : "Click the button below to continue with Google and return here with your locations."}
            </p>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl p-4" style={{ background: theme.dangerBg }}>
            <p className="text-sm font-semibold" style={{ color: theme.danger }}>
              No location selected. Please search for your business first.
            </p>
          </div>
        )}

        <div className="mt-6 space-y-3">
          {steps.map((step) => (
            <div
              key={step.id}
              className="flex items-start rounded-2xl border p-4"
              style={{ borderColor: subtleBorder, background: theme.card }}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full font-semibold text-white" style={{ background: theme.primary }}>
                {step.id}
              </div>

              <div className="ml-3 flex-1">
                <div className="flex items-center gap-2">
                  <span style={{ color: theme.primary }}>{step.icon}</span>
                  <h3 className="font-semibold" style={{ color: theme.text }}>{step.title}</h3>
                </div>

                <p className="mt-1 text-sm leading-relaxed" style={{ color: theme.textMuted }}>
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-start rounded-2xl border p-4" style={{ borderColor: subtleBorder, background: theme.card }}>
          <div className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: theme.successBg, color: theme.success }}>
            <FaShieldAlt />
          </div>

          <div className="ml-3">
            <h3 className="font-semibold" style={{ color: theme.text }}>Security Note</h3>

            <p className="mt-1 text-sm leading-relaxed" style={{ color: theme.textMuted }}>
              Your credentials are encrypted and stored securely. We never post
              without your explicit approval.
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl px-4 py-3" style={{ background: theme.dangerBg }}>
            <p className="text-sm font-medium" style={{ color: theme.danger }}>{error}</p>

            {/[Pp]lan allows a maximum|[Uu]pgrade your plan/.test(error) && (
              <Button variant="primary" theme={theme} onClick={() => navigate("/dashboard/subscription")} className="mt-3">
                Upgrade Plan
              </Button>
            )}
          </div>
        )}

        <div className="mt-8">
          <Button
            variant="primary"
            theme={theme}
            onClick={handleConnectGoogle}
            disabled={isLoading || (!isOnboardingMode && selectedLocations.length === 0)}
            className="w-full py-3"
          >
            {isLoading
              ? isOnboardingMode
                ? selectedLocations.length > 0
                  ? `Selecting ${selectedLocations.length} location${selectedLocations.length > 1 ? "s" : ""}...`
                  : "Opening Google..."
                : `${isAddLocationMode ? "Adding" : "Connecting"} ${connectedCount + 1} of ${selectedLocations.length}...`
              : isOnboardingMode && selectedLocations.length === 0
              ? "Continue with Google"
              : selectedLocations.length === 1
              ? isAddLocationMode
                ? "Add Location"
                : "Connect Google Business Profile"
              : isAddLocationMode
              ? `Add ${selectedLocations.length} Locations`
              : `Connect ${selectedLocations.length} Locations`}
          </Button>

          <button
            onClick={handleSkip}
            className="mt-4 w-full text-center font-semibold"
            style={{ color: theme.primary }}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
